import { format, getMonth, getYear, setMonth, setYear } from "date-fns";
import { enUS, ptBR } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";
import React from "react";

import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ILocale, useI18n } from "@/context/i18n";
import { cn } from "@/lib/utils";

interface DatePickerProps {
  startYear?: number;
  endYear?: number;
  value?: Date | undefined;
  onChange: (date: Date | undefined) => void;
  locale?: ILocale;
}
const localeMap = {
  "pt-BR": ptBR,
  en: enUS,
  "en-US": enUS,
};

export function DatePicker({
  startYear = getYear(new Date()) - 100,
  endYear = getYear(new Date()) + 100,
  value,
  onChange,
  locale,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  // Se não houver valor, não inicializa com data atual - usa uma data neutra (1º de janeiro do ano atual)
  const [viewDate, setViewDate] = React.useState<Date>(
    value ?? new Date(new Date().getFullYear(), 0, 1)
  );
  const { t, locale: i18nLocale } = useI18n();
  const dateFnsLocale = localeMap[locale ?? i18nLocale] ?? enUS;

  const months = [
    t("components.date-picker.months.january"),
    t("components.date-picker.months.february"),
    t("components.date-picker.months.march"),
    t("components.date-picker.months.april"),
    t("components.date-picker.months.may"),
    t("components.date-picker.months.june"),
    t("components.date-picker.months.july"),
    t("components.date-picker.months.august"),
    t("components.date-picker.months.september"),
    t("components.date-picker.months.october"),
    t("components.date-picker.months.november"),
    t("components.date-picker.months.december"),
  ];
  const years = Array.from(
    { length: endYear - startYear + 1 },
    (_, i) => startYear + i
  );

  const handleMonthChange = (month: string) => {
    const newDate = setMonth(viewDate, months.indexOf(month));
    setViewDate(newDate);
  };

  const handleYearChange = (year: string) => {
    const newDate = setYear(viewDate, parseInt(year));
    setViewDate(newDate);
  };

  const handleSelect = (selectedData: Date | undefined) => {
    if (selectedData) {
      setViewDate(selectedData);
      onChange(selectedData);
      setOpen(false);
    }
  };

  // Keep internal view month/year in sync when external value changes
  React.useEffect(() => {
    if (value) {
      setViewDate(value);
    }
  }, [value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "w-[250px] justify-start text-left font-normal flex items-center border border-input bg-background text-foreground rounded-md px-3 py-2",
            !value && "text-muted-foreground"
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? (
            format(value, "PPP", { locale: dateFnsLocale })
          ) : (
            <span>Escolha a data</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <div className="flex justify-between p-2">
          <Select
            onValueChange={handleMonthChange}
            value={months[getMonth(viewDate)]}
          >
            <SelectTrigger className="w-[110px]">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              {months.map((month) => (
                <SelectItem key={month} value={month}>
                  {month}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            onValueChange={handleYearChange}
            value={getYear(viewDate).toString()}
          >
            <SelectTrigger className="w-[110px]">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              {years.map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Calendar
          locale={dateFnsLocale}
          mode="single"
          selected={value}
          onSelect={handleSelect}
          month={viewDate}
          onMonthChange={setViewDate}
        />
      </PopoverContent>
    </Popover>
  );
}
